import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useLabTests, useLabPackages, useLabDepartments } from './hooks';
import type { LabPackage, LabTest } from './types';
import { OutsideLabBadge } from './ui';

/**
 * Picking what to order.
 *
 * Two things it deliberately shows next to each test, because both change
 * what happens at the couch and neither is recoverable afterwards:
 *   * the SAMPLE TYPE and CONTAINER — so the person ordering can see that
 *     adding an LFT means a second tube, before the patient is stuck twice;
 *   * FASTING and preparation — a fasting test ordered on a patient who just
 *     ate is a wasted draw and a wasted visit.
 *
 * Prices come from the master and are shown, but nothing here computes a
 * total the patient is quoted: the invoice is raised by the existing billing
 * engine server-side, and a second number computed in the browser is a
 * number that can disagree with the bill.
 */

export interface SelectedTests {
  testIds: number[];
  packageIds: number[];
}

export interface TestSelectorProps {
  value: SelectedTests;
  onChange: (next: SelectedTests) => void;
}

export default function TestSelector({ value, onChange }: TestSelectorProps) {
  const [q, setQ] = useState('');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const departments = useLabDepartments();
  const tests = useLabTests({ q: q.trim() || undefined, department_id: departmentId || undefined });
  const packages = useLabPackages();

  const selectedTestSet = useMemo(() => new Set(value.testIds), [value.testIds]);
  const selectedPackageSet = useMemo(() => new Set(value.packageIds), [value.packageIds]);

  const toggleTest = (t: LabTest) => {
    const next = new Set(selectedTestSet);
    if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
    onChange({ ...value, testIds: [...next] });
  };
  const togglePackage = (p: LabPackage) => {
    const next = new Set(selectedPackageSet);
    if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
    onChange({ ...value, packageIds: [...next] });
  };

  const chosenTests = (tests.data ?? []).filter((t) => selectedTestSet.has(t.id));
  const chosenPackages = (packages.data ?? []).filter((p) => selectedPackageSet.has(p.id));

  // Which distinct tubes this selection implies. Shown as a summary because
  // "three tests" and "three tubes" are different facts and only the second
  // one matters to the patient's arm.
  const tubes = useMemo(() => {
    const names = new Set<string>();
    chosenTests.forEach((t) => { if (t.sample_type_name) names.add(t.sample_type_name); });
    return [...names];
  }, [chosenTests]);

  const fasting = chosenTests.filter((t) => t.fasting_required);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Input
          placeholder="Search tests by name or code…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="min-w-[220px] flex-1"
        />
        <select
          className="h-9 rounded-lg border border-border bg-card px-2 text-[13px]"
          value={departmentId}
          onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : '')}
        >
          <option value="">All departments</option>
          {(departments.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      {(packages.data ?? []).length > 0 && (
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Packages</div>
          <div className="flex flex-wrap gap-2">
            {(packages.data ?? []).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => togglePackage(p)}
                className={`rounded-lg border px-3 py-1.5 text-[12px] ${
                  selectedPackageSet.has(p.id) ? 'border-teal bg-teal/10 font-semibold' : 'border-border hover:bg-muted'
                }`}
              >
                {p.name} · Rs. {Number(p.price).toFixed(0)}
                <span className="ml-1 text-muted-foreground">({p.tests.length} tests)</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-h-[260px] overflow-auto rounded-lg border border-border">
        {tests.isLoading && <div className="p-3 text-[13px] text-muted-foreground">Loading tests…</div>}
        {!tests.isLoading && (tests.data ?? []).length === 0 && (
          <div className="p-3 text-[13px] text-muted-foreground">No tests match that search.</div>
        )}
        {(tests.data ?? []).map((t) => (
          <label
            key={t.id}
            className="flex cursor-pointer items-start gap-2 border-b border-border px-3 py-2 last:border-0 hover:bg-muted/50"
          >
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0"
              checked={selectedTestSet.has(t.id)}
              onChange={() => toggleTest(t)}
            />
            <span className="min-w-0 flex-1">
              <span className="text-[13px] font-medium">{t.name}</span>
              <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">{t.code}</span>
              {t.fasting_required && <Badge variant="warn" className="ml-1.5">Fasting</Badge>}
              <OutsideLabBadge processingType={t.processing_type} referenceLabName={t.reference_lab_name} />
              <span className="block text-[11px] text-muted-foreground">
                {[t.department_name, t.sample_type_name, t.container_name,
                  t.tat_hours ? `TAT ${Number(t.tat_hours)}h` : null]
                  .filter(Boolean).join(' · ')}
              </span>
            </span>
            <span className="shrink-0 whitespace-nowrap text-[12px] tabular-nums text-muted-foreground">
              Rs. {Number(t.price).toFixed(0)}
            </span>
          </label>
        ))}
      </div>

      {(chosenTests.length > 0 || chosenPackages.length > 0) && (
        <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-[12px]">
          <div className="font-semibold">
            {chosenTests.length + chosenPackages.length} selected
            {chosenPackages.length > 0 && ` (${chosenPackages.length} package${chosenPackages.length === 1 ? '' : 's'})`}
          </div>
          {tubes.length > 0 && (
            <div className="mt-0.5 text-muted-foreground">
              Sample{tubes.length === 1 ? '' : 's'} to draw: {tubes.join(', ')}
            </div>
          )}
          {fasting.length > 0 && (
            <div className="mt-0.5 text-warn-ink">
              Fasting required for: {fasting.map((t) => t.name).join(', ')}
            </div>
          )}
          <Button
            variant="link"
            size="sm"
            className="mt-1"
            onClick={() => onChange({ testIds: [], packageIds: [] })}
          >
            Clear selection
          </Button>
        </div>
      )}
    </div>
  );
}
